import { validWidgetColor } from '@shared/theme/widgetTheme';
import type { WidgetConfiguration } from './widgetApi';
export const positiveCredits = (raw: string) =>
  /^(?:0|[1-9][0-9]{0,37})(?:\.[0-9]{1,9})?$/.test(raw) && /[1-9]/.test(raw);
const decimal = (v: string) => {
  const [whole, fraction = ''] = v.split('.');
  return BigInt(whole) * 1000000000n + BigInt(fraction.padEnd(9, '0'));
};
export const normalizedOrigin = (raw: string) => {
  try {
    const u = new URL(raw.trim());
    return (u.protocol === 'https:' ||
      (u.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(u.hostname))) &&
      !u.hostname.includes('*') &&
      !u.username &&
      !u.password &&
      !u.search &&
      !u.hash &&
      u.pathname === '/'
      ? u.origin
      : null;
  } catch {
    return null;
  }
};
export const httpsUrl = (raw: string) => {
  try {
    const u = new URL(raw);
    return u.protocol === 'https:' && !u.username && !u.password;
  } catch {
    return false;
  }
};
export const widgetConfigurationError = (v: WidgetConfiguration) => {
  if (
    v.allowedOrigins.some((x) => normalizedOrigin(x) !== x) ||
    new Set(v.allowedOrigins).size !== v.allowedOrigins.length ||
    (v.enabled && !v.allowedOrigins.length)
  )
    return 'Enter unique HTTPS origins (scheme and hostname, optional port), with no path. Localhost HTTP is allowed for testing.';
  if (!positiveCredits(v.budget.creditsPerSession) || !positiveCredits(v.budget.creditsPerDay))
    return 'Enter positive exact credit caps with at most 9 fractional digits.';
  if (decimal(v.budget.creditsPerSession) > decimal(v.budget.creditsPerDay))
    return 'Session credits must not exceed daily credits.';
  if (
    !Number.isSafeInteger(v.budget.maxConcurrentSessions) ||
    v.budget.maxConcurrentSessions < 1 ||
    v.budget.maxConcurrentSessions > 100
  )
    return 'Concurrent sessions must be an integer from 1 to 100.';
  if (
    !Number.isSafeInteger(v.budget.maxSessionSeconds) ||
    v.budget.maxSessionSeconds < 30 ||
    v.budget.maxSessionSeconds > 3600
  )
    return 'Session duration must be an integer from 30 to 3600 seconds.';
  if (Object.values(v.appearance.colors).some((c) => c !== undefined && !validWidgetColor(c)))
    return 'Colors must contain exactly six hexadecimal digits prefixed by #.';
  for (const [k, max] of [
    ['title', 120],
    ['cta', 120],
    ['start', 80],
    ['end', 80],
    ['placeholder', 160],
  ] as const)
    if (!v.appearance[k].trim() || v.appearance[k].length > max)
      return `Enter ${k} (maximum ${max} characters).`;
  if (v.appearance.termsEnabled && (!v.appearance.termsUrl || !httpsUrl(v.appearance.termsUrl)))
    return 'Enabled terms require an HTTPS URL.';
  if (
    v.appearance.avatar &&
    !httpsUrl(v.appearance.avatar) &&
    !/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/.test(v.appearance.avatar)
  )
    return 'Use an HTTPS avatar URL or upload a PNG, JPEG or WebP.';
  return '';
};
export const blankWidget = (): WidgetConfiguration => ({
  enabled: false,
  allowedOrigins: [],
  budget: {
    creditsPerSession: '',
    creditsPerDay: '',
    maxConcurrentSessions: 0,
    maxSessionSeconds: 0,
  },
  appearance: {
    mode: 'chat',
    size: 'full',
    placement: 'bottom-right',
    colors: {},
    title: 'Assistant',
    cta: 'Talk to us',
    start: 'Start',
    end: 'End',
    placeholder: 'Type your message…',
    avatar: null,
    termsEnabled: false,
    termsUrl: null,
  },
});
